import { useEffect } from "react";

import { useTranslation } from "react-i18next";
import { App, Flex } from "antd";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import {
  useCreateDiscussionMutation,
  useGetTopicsQuery,
  useLazyGetDiscussionByIdQuery,
  useUpdateDiscussionMutation,
  useLazyGetSubjectsQuery,
} from "../../redux/service.js";
import { handleErrors } from "@/shared/utils/forms";

import ContentField from "./components/ContentField.jsx";
import BaseButton from "../../components_v2/Button/BaseButton.js";

import "./styles.css";
import { HookFormBaseDropdown } from "../../components_v2/FormFields/ReactHookForm/fields";
import {
  HookFormCheckbox,
  HookFormInput,
} from "../../components_v2/FormFields/ReactHookForm/fields.js";
import { selectCurrentUser } from "@/redux/authentication/selectors.js";
import {
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants.js";
import { MediaUploadProvider, useMediaUploadGate } from "@/shared/media";

const discussionSchema = (t) =>
  yup.object().shape({
    title: yup
      .string()
      .typeError(t("form.validation.titleNotValid"))
      .required(t("form.validation.titleRequired")),
    content: yup
      .string()
      .typeError(t("form.validation.bodyNotValid"))
      .required(t("form.validation.bodyRequired")),
    topicIds: yup
      .array()
      .typeError(t("form.validation.topicsNotValid"))
      .required(t("form.validation.topicsRequired"))
      .min(1, t("form.validation.topicsMinimum")),
    subjects: yup
      .array()
      .typeError(t("form.validation.tagsNotValid"))
      .required(t("form.validation.tagsRequired"))
      .min(1, t("form.validation.tagsMinimum")),
    blocks: yup.array(),
    surveyViews: yup.array(),
  });

const DiscussionSubmitButton = ({ disabled, onClick, children }) => {
  const hasUploads = useMediaUploadGate();
  return (
    <BaseButton
      variant={BUTTON_VARIANTS.PRIMARY}
      disabled={disabled || hasUploads}
      onClick={onClick}
    >
      {children}
    </BaseButton>
  );
};

DiscussionSubmitButton.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

const DiscussionFormInner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const { discussionId } = useParams();
  const location = useLocation();

  const [getDiscussionById, { data: existingDiscussionData }] =
    useLazyGetDiscussionByIdQuery();
  const [createDiscussion, createDiscussionResponse] =
    useCreateDiscussionMutation();
  const [updateDiscussion] = useUpdateDiscussionMutation();
  const [
    getSubjects,
    { data: subjectsResponse, isSuccess: subjectsIsSuccess },
  ] = useLazyGetSubjectsQuery();
  const { data: topicsResponse, isSuccess: topicsIsSuccess } =
    useGetTopicsQuery({ pagination: "off" });
  const currentUser = useSelector(selectCurrentUser);

  const form = useForm({
    resolver: yupResolver(discussionSchema(t)),
    mode: "onChange",
    defaultValues: {
      title: "",
      topicIds: null,
      subjects: null,
      content: "",
      blocks: [],
      surveyViews: [],
    },
  });

  useEffect(() => {
    if (location.state?.dataBlock) {
      form.setValue("blocks", [location.state.dataBlock]);
    }
    if (location.state?.surveyView) {
      form.setValue("surveyViews", [location.state.surveyView]);
    }
  }, [location]);

  useEffect(() => {
    if (discussionId) {
      getDiscussionById(discussionId);
    } else if (currentUser) {
      getSubjects({
        pagination: "off",
        organization: currentUser.organization?.id,
      });
    }
  }, [currentUser, discussionId]);

  useEffect(() => {
    if (existingDiscussionData) {
      form.reset({
        title: existingDiscussionData.title,
        topicIds: existingDiscussionData.topics,
        subjects: existingDiscussionData.subjects.map((i) => i.name),
        content: existingDiscussionData.content,
        blocks: existingDiscussionData.attachedBlocks,
        surveyViews: existingDiscussionData.surveyViews,
      });
      getSubjects({
        pagination: "off",
      });
    }
  }, [existingDiscussionData]);

  const handleSubmit = (values) => {
    let mutationFunction;
    const data = {
      ...values,
      attachedBlockIds: values.blocks.map((i) => i.id),
      attachedSurveyViewUuids: values.surveyViews.map((i) => i.id),
      subjects: values.subjects.map((i) => ({ name: i })),
    };
    if (discussionId) {
      mutationFunction = updateDiscussion({ id: discussionId, data });
    } else {
      mutationFunction = createDiscussion(data);
    }

    mutationFunction
      .unwrap()
      .then((data) => {
        navigate(`/discussions/${data.id}`);
        notification.success({
          message: discussionId
            ? t("discussionUpdated")
            : t("newDiscussionCreated"),
        });
      })
      .catch((errors) => handleErrors(errors, form.setError, notification, t));
  };

  return (
    <Flex vertical gap={24} className="discussion-form">
      <h1 className="header-l-medium">
        {discussionId ? t("updateDiscussion") : t("newDiscussion")}
      </h1>

      <Flex vertical gap={16}>
        <Flex vertical gap={4}>
          <div className="body-s-medium">{t("form.labels.title")}</div>

          <HookFormInput
            name="title"
            placeholder={t("form.placeholders.title")}
            control={form.control}
          />
        </Flex>

        <Flex vertical gap={4}>
          <div className="body-s-medium">{t("form.labels.topics")}</div>

          <HookFormBaseDropdown
            mode="multiple"
            name="topicIds"
            options={
              topicsIsSuccess
                ? topicsResponse.map((i) => ({
                    value: i.id,
                    label: i.title,
                  }))
                : []
            }
            placeholder={t("form.placeholders.topics")}
            control={form.control}
          />
        </Flex>

        <Flex vertical gap={4}>
          <div className="body-s-medium">{t("form.labels.subjects")}</div>

          <HookFormBaseDropdown
            mode="tags"
            name="subjects"
            options={
              subjectsIsSuccess
                ? subjectsResponse.map((i) => ({
                    value: i.name,
                    label: i.name,
                  }))
                : []
            }
            placeholder={t("form.placeholders.subjectsSelect")}
            control={form.control}
          />
        </Flex>

        <ContentField form={form} label={t("form.labels.body")}>
          <Flex vertical gap={"small"}>
            <Flex wrap justify="space-between">
              <BaseButton
                variant={BUTTON_VARIANTS.TERTIARY}
                type={BUTTON_TYPES.LINK}
                to={"/community"}
              >
                {t("buttons.cancel")}
              </BaseButton>

              <DiscussionSubmitButton
                disabled={createDiscussionResponse.isLoading}
                onClick={form.handleSubmit(handleSubmit)}
              >
                {t("buttons.post")}
              </DiscussionSubmitButton>
            </Flex>
          </Flex>
        </ContentField>
      </Flex>
    </Flex>
  );
};

const DiscussionForm = () => (
  <MediaUploadProvider>
    <DiscussionFormInner />
  </MediaUploadProvider>
);

export default DiscussionForm;
